import { useQuery, useMutation } from '@tanstack/react-query';
import { publicService } from '@/api/services/public.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import type { PaginationParams } from '@/api/types';
import type { ContactMessageRequest } from '@/api/types/common.types';

export const usePublicProjects = (params?: PaginationParams) => {
  return useQuery({
    queryKey: queryKeys.public.projects(params),
    queryFn: () => publicService.getProjects(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const usePublicProject = (id: number) => {
  return useQuery({
    queryKey: queryKeys.public.project(id),
    queryFn: () => publicService.getProjectDetails(id),
    enabled: !!id,
  });
};

export const useCompanyInfo = () => {
  return useQuery({
    queryKey: queryKeys.public.company(),
    queryFn: () => publicService.getCompanyInfo(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useContactMessage = () => {
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: ContactMessageRequest) =>
      publicService.sendContactMessage(data),
    onSuccess: () => {
      toast({
        title: t('contact.messageSent'),
        description: t('contact.messageSentDesc'),
      });
    },
    onError: (error) => {
      toast({
        title: t('contact.error') || 'فشل إرسال الرسالة',
        description: error instanceof Error ? error.message : t('common.error') || 'حدث خطأ غير متوقع',
        variant: 'destructive',
      });
    },
  });
};

// Public Content Hooks
export const usePublicHomepageContent = () => {
  return useQuery({
    queryKey: queryKeys.public.content.homepage(),
    queryFn: () => publicService.getHomepageContent(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const usePublicAboutContent = () => {
  return useQuery({
    queryKey: queryKeys.public.content.about(),
    queryFn: () => publicService.getAboutContent(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicHowItWorksContent = () => {
  return useQuery({
    queryKey: queryKeys.public.content.howItWorks(),
    queryFn: () => publicService.getHowItWorksContent(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicFAQs = () => {
  return useQuery({
    queryKey: queryKeys.public.content.faqs(),
    queryFn: () => publicService.getFAQs(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicContactContent = () => {
  return useQuery({
    queryKey: queryKeys.public.content.contact(),
    queryFn: () => publicService.getContactContent(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicNavigationLinks = () => {
  return useQuery({
    queryKey: queryKeys.public.content.navigation(),
    queryFn: () => publicService.getNavigationLinks(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicFooterContent = () => {
  return useQuery({
    queryKey: queryKeys.public.content.footer(),
    queryFn: () => publicService.getFooterContent(),
    staleTime: 10 * 60 * 1000,
  });
};

export const usePublicSpecializations = () => {
  return useQuery({
    queryKey: queryKeys.public.specializations(),
    queryFn: () => publicService.getSpecializations(),
    staleTime: 10 * 60 * 1000,
  });
};
